import { Button, Modal, TextOrHTML } from "@simplybusiness/mobius";
import { DialogDrawer } from "./DialogDrawer";
import { useDialogueModal } from "./useDialogueModal";

const PRODUCT_NAME_MAP: Record<string, string> = {
  "General liability": "General Liability",
  "Professional liability": "Professional Liability / Errors & Omissions",
  Cyber: "Cyber",
  "Inland marine": "Business Equipment and Small Tools",
  "Business Owners Policy": "Business Owners Policy",
};

const DEFAULT_CHECKBOX_CONTAINER = ".s-step-recommendations";

export type DialogueModalProps = {
  /** Product name to display in the modal header */
  name: string;
  /** Field name used to identify the checkbox */
  fieldName: string;
  /** HTML content describing the product */
  productInfo: string;
  /** Text for the trigger button */
  text?: string;
  /** Whether to render as a Drawer instead of Modal */
  isDrawer?: boolean;
  /** CSS selector for the container with coverage checkboxes */
  checkboxContainer?: string;
  /** Callback fired when modal is opened */
  onOpen?: (fieldName: string) => void;
  /** Callback fired when coverage is added */
  onCoverageAdd?: (fieldName: string) => void;
  /** Callback fired when coverage is removed */
  onCoverageRemove?: (fieldName: string) => void;
};

export const DialogueModal = ({
  name,
  fieldName,
  productInfo,
  text = "Learn more",
  isDrawer = false,
  checkboxContainer = DEFAULT_CHECKBOX_CONTAINER,
  onOpen,
  onCoverageAdd,
  onCoverageRemove,
}: DialogueModalProps) => {
  const {
    isOpen,
    isCoverageAdded,
    handleClose,
    handleCtaClick,
    handleTriggerClick,
  } = useDialogueModal({
    fieldName,
    checkboxContainer,
    onOpen,
    onCoverageAdd,
    onCoverageRemove,
  });

  const displayName = PRODUCT_NAME_MAP[name] ?? name;

  return (
    <div className="mobius mobius-dialogue-modal">
      <Button
        className="mobius-dialogue-modal__trigger"
        variant="basic"
        size="sm"
        onClick={handleTriggerClick}
      >
        {text}
      </Button>

      {isDrawer ? (
        <DialogDrawer
          displayName={displayName}
          productInfo={productInfo}
          isOpen={isOpen}
          isCoverageAdded={isCoverageAdded}
          onClose={handleClose}
          onCtaClick={handleCtaClick}
        />
      ) : (
        <Modal isOpen={isOpen} onClose={handleClose} animation="slideUp">
          <Modal.Header>{displayName}</Modal.Header>
          <Modal.Content>
            <div className="mobius-dialogue-modal__body">
              <TextOrHTML text={productInfo} htmlElementType="div" />
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
};

DialogueModal.displayName = "DialogueModal";
